// Scheme-guards for customer-supplied contact/link data (task-033).
//
// WHY THIS EXISTS (security): a location's website and image URLs are UNTRUSTED
// customer data. Turning them into hrefs verbatim would let a `javascript:` or
// `data:` URL execute in the widget's context the moment a visitor clicks. This
// widget embeds on arbitrary customer pages, so that is a real XSS surface. Every
// such value passes through these guards, which only ever emit an href for a
// scheme we explicitly trust — http/https for links, and purpose-built builders
// for `tel:`/`mailto:` that sanitize their input. Anything else returns null and
// the caller renders no anchor at all (an honest omission, not a broken link).

/**
 * Returns [raw] as an href ONLY when it parses to an http/https URL; otherwise
 * null. Rejects `javascript:`, `data:`, `mailto:`, `tel:`, relative URLs, and
 * anything unparseable — those either have their own explicit builders below or
 * are simply not safe to link.
 */
export function safeHttpUrl(raw: string | null | undefined): string | null {
  if (raw == null) {
    return null;
  }
  const trimmed = raw.trim();
  if (trimmed === "") {
    return null;
  }
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    // Not an absolute URL (relative, malformed, or a bare scheme) — reject.
    return null;
  }
  if (url.protocol === "http:" || url.protocol === "https:") {
    return url.href;
  }
  return null;
}

/**
 * Builds a `tel:` href from a phone string, keeping only a leading `+` and
 * digits (dialers ignore spaces/parentheses/dashes anyway, and stripping them
 * removes any room for scheme injection). Null when nothing dialable remains.
 */
export function telHref(phone: string | null | undefined): string | null {
  if (phone == null) {
    return null;
  }
  const plus = phone.trim().startsWith("+") ? "+" : "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 0) {
    return null;
  }
  return `tel:${plus}${digits}`;
}

/**
 * Builds a `mailto:` href only when [email] looks like an email address. The
 * shape check (no whitespace, a single `@`, a dotted domain) both validates and
 * guarantees there are no characters that could break out of the scheme.
 */
export function mailtoHref(email: string | null | undefined): string | null {
  if (email == null) {
    return null;
  }
  const trimmed = email.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return null;
  }
  return `mailto:${trimmed}`;
}
